import {
  Component,
  inject,
  Inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { AbbSidePanelComponent } from '@components/abb-side-panel/abb-side-panel.component';
import {
  SIDE_PANEL_DATA,
  SidePanelData,
} from '@components/abb-side-panel/side-panel';
import { AbbCommonModule } from 'app/abb-common.module';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { ToastService } from '@services/ui/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { Apollo } from 'apollo-angular';
import {
  CreateReportTemplateMutation,
  CreateReportTemplateMutationVariables,
  ReportTemplateInput,
  ReportTemplateListQuery,
  UpdateReportTemplateMutation,
  UpdateReportTemplateMutationVariables,
} from 'gql/graphql';
import {
  createReportTemplateMutation,
  updateReportTemplateMutation,
} from '@graphql/report';
import { ReportTemplateFormComponent } from '../report-template-form/report-template-form.component';
import { firstValueFrom } from 'rxjs';
import { defer, ignoreErrorAsync } from '@utils/runtime';
import { switchCase } from '@utils/control-flow';
import { t } from '@utils/strings';

type ReportTemplateSidePanelData = {
  onCancel?(): void;
  onSave?(report: ReportTemplateListQuery['reportTemplateList'][number]): void;
} & (
  | { mode: 'new'; template?: undefined }
  | {
      mode: 'edit' | 'clone';
      template: ReportTemplateInput & { id: string };
    }
);

@Component({
  selector: 'abb-report-template-side-panel',
  standalone: true,
  // TODO: improve min-w mechanism
  template: `
    <abb-side-panel
      id="new-report-template-panel"
      [hasBackdrop]="false"
      contentWrapperClass="min-w-[787px]"
      [title]="
        (data.mode === 'edit' ? 'Edit Report Template' : 'New Report Template')
          | t
      "
      #panelRef
    >
      <abb-report-template-form
        [mode]="data.mode"
        [initValues]="data.template"
        (abbSubmit)="onSubmit($event)"
      ></abb-report-template-form>
      <div footer class="w-full flex flex-row gap-8 justify-end p-8">
        <abb-button
          variant="clean"
          [disabled]="saving()"
          (onclick)="onCancel()"
          >{{ 'cancel' | t }}</abb-button
        >
        <abb-button
          class="w-32"
          type="submit"
          form="report-template-form"
          [loading]="saving()"
          [disabled]="saving()"
        >
          @switch (data.mode) { @case ('new') {
          {{ 'Create' | t }}
          } @case ('edit') {
          {{ 'Save' | t }}
          } @case ('clone') {
          {{ 'Clone' | t }}
          } }
        </abb-button>
      </div>
    </abb-side-panel>
  `,
  styles: ``,
  imports: [
    AbbCommonModule,
    AbbSidePanelComponent,
    ReportTemplateFormComponent,
    TPipe,
  ],
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'reports',
    },
  ],
})
export class ReportTemplateSidePanelComponent
  implements SidePanelData<ReportTemplateSidePanelData>
{
  panelRef = viewChild<AbbSidePanelComponent>('panelRef');
  saving = signal(false);

  prefix = inject(TRANSLATION_PREFIX);
  constructor(
    @Inject(SIDE_PANEL_DATA) public data: ReportTemplateSidePanelData,
    private readonly apollo: Apollo,
    private readonly toastService: ToastService,
    private readonly translate: TranslateService
  ) {}

  onCancel() {
    this.panelRef()?.dispose();
  }

  async onSubmit(payload: ReportTemplateInput) {
    this.saving.set(true);
    try {
      const template = await switchCase(this.data.mode, {
        new: () =>
          firstValueFrom(
            this.apollo.mutate({
              mutation: createReportTemplateMutation,
              variables: {
                input: payload,
              },
            })
          ).then((x) => x.data!.createReportTemplate),
        edit: () =>
          firstValueFrom(
            this.apollo.mutate({
              mutation: updateReportTemplateMutation,
              variables: {
                input: { id: this.data.template!.id, ...payload },
              },
            })
          ).then((x) => x.data!.updateReportTemplate),
        clone: () =>
          firstValueFrom(
            this.apollo.mutate({
              mutation: createReportTemplateMutation,
              variables: {
                input: payload,
              },
            })
          ).then((x) => x.data!.createReportTemplate),
      });

      this.toastService.notifySuccess(
        t(this.prefix, `{name} saved successfully`, this.translate, {
          name: payload.name,
        })
      );
      this.data.onSave?.(template);
      await ignoreErrorAsync(() =>
        this.apollo.client.refetchQueries({
          include: ['ReportTemplateList'],
        })
      );
      defer(() => this.panelRef()?.handleClose());
    } catch {
      this.toastService.notifyError(
        t(
          this.prefix,
          `An error occurred while saving {name}`,
          this.translate,
          { name: payload.name }
        )
      );
    } finally {
      this.saving.set(false);
    }
  }
}
